import React from "react";
import { Card, Select } from "antd";
import { Grid } from "@material-ui/core";
import HourglassEmpty from "@material-ui/icons/HourglassEmpty";
import Timeline from "@material-ui/icons/Timeline";
import ShowChart from "@material-ui/icons/ShowChart";
import { GetData } from "../../api/service";

const COLORS = {
  text: "#1F2937",
  muted: "#6B7280",
  border: "#E5E7EB",
  amberBg: "#FEF3C7",
  amberFg: "#92400E",
  redBg: "#FEE2E2",
  redFg: "#991B1B",
  emeraldBg: "#D1FAE5",
  emeraldFg: "#065F46",
  chartBar: "#7C3AED",
  chartBarSoft: "#EDE9FE",
};

function ActionCard({ tone, icon, label, value, hint }) {
  const tones = {
    red: { bg: COLORS.redBg, fg: COLORS.redFg },
    amber: { bg: COLORS.amberBg, fg: COLORS.amberFg },
    emerald: { bg: COLORS.emeraldBg, fg: COLORS.emeraldFg },
  };
  const t = tones[tone] || tones.amber;
  return (
    <div
      style={{
        background: "white",
        borderRadius: 12,
        padding: "18px 20px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        border: `1px solid ${COLORS.border}`,
        display: "flex",
        alignItems: "center",
        gap: 16,
        minHeight: 110,
      }}
    >
      <div
        style={{
          background: t.bg,
          color: t.fg,
          width: 52,
          height: 52,
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase" }}>
          {label}
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.text, lineHeight: 1.15, marginTop: 4 }}>
          {value}
        </div>
        {hint && (
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>{hint}</div>
        )}
      </div>
    </div>
  );
}

function TrendChart({ data, total }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const max = Math.max(1, ...data.map((d) => d.count));
  const width = 100 / data.length;

  return (
    <div
      style={{
        background: "white",
        borderRadius: 12,
        padding: "18px 22px 14px 22px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        border: `1px solid ${COLORS.border}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Timeline style={{ color: COLORS.chartBar, fontSize: 22 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
              Activations — last 30 days
            </div>
            <div style={{ fontSize: 11, color: COLORS.muted }}>
              Daily count of newly activated users
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, lineHeight: 1 }}>
            {total}
          </div>
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>total in 30d</div>
        </div>
      </div>

      <svg viewBox="0 0 100 40" preserveAspectRatio="none" style={{ width: "100%", height: 120, display: "block" }}>
        {data.map((d, i) => {
          const h = (d.count / max) * 36;
          const x = i * width + width * 0.15;
          const w = width * 0.7;
          const y = 40 - h;
          return (
            <g key={d.date}>
              <rect x={x} y={0} width={w} height={40} fill={COLORS.chartBarSoft} rx={0.5} />
              <rect x={x} y={y} width={w} height={h} fill={COLORS.chartBar} rx={0.5}>
                <title>{`${d.date} — ${d.count} activation${d.count === 1 ? "" : "s"}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
          color: COLORS.muted,
          marginTop: 8,
        }}
      >
        <span>{data[0].date}</span>
        <span>{data[Math.floor(data.length / 2)].date}</span>
        <span>{data[data.length - 1].date}</span>
      </div>
    </div>
  );
}

function MonthlyLineChart() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = React.useState(currentYear);
  const [state, setState] = React.useState({
    loaded: false,
    monthly: [],
    total: 0,
    available_years: [currentYear],
  });

  React.useEffect(() => {
    setState((s) => ({ ...s, loaded: false }));
    GetData(`/monthly-activations?year=${year}`)
      .then((resp) => {
        if (resp && typeof resp === "object") {
          setState({
            loaded: true,
            monthly: Array.isArray(resp.monthly) ? resp.monthly : [],
            total: resp.total || 0,
            available_years:
              Array.isArray(resp.available_years) && resp.available_years.length > 0
                ? resp.available_years
                : [currentYear],
          });
        }
      })
      .catch(() => {
        setState((s) => ({ ...s, loaded: true }));
      });
  }, [year, currentYear]);

  const data = state.monthly;
  const max = Math.max(1, ...data.map((d) => d.count));

  // SVG viewBox
  const W = 800;
  const H = 220;
  const padX = 40;
  const padTop = 20;
  const padBottom = 34;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;

  const pointX = (i) => padX + (data.length > 1 ? (i * innerW) / (data.length - 1) : innerW / 2);
  const pointY = (v) => padTop + innerH - (v / max) * innerH;

  const linePath = data
    .map((d, i) => `${i === 0 ? "M" : "L"} ${pointX(i).toFixed(2)} ${pointY(d.count).toFixed(2)}`)
    .join(" ");

  const areaPath =
    data.length > 0
      ? `${linePath} L ${pointX(data.length - 1).toFixed(2)} ${(padTop + innerH).toFixed(2)} L ${pointX(0).toFixed(2)} ${(padTop + innerH).toFixed(2)} Z`
      : "";

  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((max * i) / yTicks));

  return (
    <div
      style={{
        background: "white",
        borderRadius: 12,
        padding: "18px 22px 14px 22px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        border: `1px solid ${COLORS.border}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ShowChart style={{ color: COLORS.chartBar, fontSize: 22 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
              Monthly Activations — {year}
            </div>
            <div style={{ fontSize: 11, color: COLORS.muted }}>
              Uses activation date, or registration date when activation is missing
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, lineHeight: 1 }}>
              {state.total}
            </div>
            <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>
              total in {year}
            </div>
          </div>
          <Select
            value={year}
            style={{ width: 110 }}
            onChange={(v) => setYear(v)}
            options={(state.available_years.includes(currentYear)
              ? state.available_years
              : [...state.available_years, currentYear].sort((a, b) => a - b)
            ).map((y) => ({ value: y, label: String(y) }))}
          />
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: 240, display: "block" }}>
        <defs>
          <linearGradient id="lineGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={COLORS.chartBar} stopOpacity="0.28" />
            <stop offset="100%" stopColor={COLORS.chartBar} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {tickValues.map((v, i) => {
          const y = pointY(v);
          return (
            <g key={i}>
              <line x1={padX} y1={y} x2={W - padX} y2={y} stroke="#F1F5F9" strokeWidth="1" />
              <text
                x={padX - 8}
                y={y + 3}
                fontSize="10"
                fill={COLORS.muted}
                textAnchor="end"
                fontFamily="monospace"
              >
                {v}
              </text>
            </g>
          );
        })}

        {data.length > 0 && (
          <>
            <path d={areaPath} fill="url(#lineGradient)" />
            <path
              d={linePath}
              fill="none"
              stroke={COLORS.chartBar}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {data.map((d, i) => (
              <g key={d.month}>
                <circle
                  cx={pointX(i)}
                  cy={pointY(d.count)}
                  r="4"
                  fill="white"
                  stroke={COLORS.chartBar}
                  strokeWidth="2"
                >
                  <title>{`${d.month_label} ${year} — ${d.count} activation${d.count === 1 ? "" : "s"}`}</title>
                </circle>
              </g>
            ))}
          </>
        )}

        {data.map((d, i) => (
          <text
            key={"lbl-" + d.month}
            x={pointX(i)}
            y={H - 10}
            fontSize="11"
            fill={COLORS.muted}
            textAnchor="middle"
          >
            {d.month_label}
          </text>
        ))}
      </svg>
    </div>
  );
}

export default function DashboardExtras() {
  const [state, setState] = React.useState({
    loaded: false,
    pending_activations: 0,
    missing_activation_date: 0,
    activations_30d_total: 0,
    activations_30d: [],
  });

  React.useEffect(() => {
    GetData(`/dashboard-extras`)
      .then((resp) => {
        if (resp && typeof resp === "object") {
          setState({ loaded: true, ...resp });
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
          color: COLORS.text,
          fontWeight: 600,
          fontSize: 15,
          letterSpacing: 0.2,
        }}
      >
        <span
          style={{
            width: 4,
            height: 18,
            background: COLORS.chartBar,
            borderRadius: 2,
            display: "inline-block",
          }}
        />
        Operations & Health
      </div>

      <Grid container spacing={16}>
        <Grid item xs={12} sm={6} md={6}>
          <ActionCard
            tone="red"
            icon={<HourglassEmpty style={{ fontSize: 26 }} />}
            label="Pending Activations"
            value={state.pending_activations}
            hint="Registrations awaiting admin approval"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <ActionCard
            tone="emerald"
            icon={<Timeline style={{ fontSize: 26 }} />}
            label="Activations — 30d"
            value={state.activations_30d_total}
            hint="Users activated in the last 30 days"
          />
        </Grid>

        <Grid item xs={12}>
          <TrendChart data={state.activations_30d} total={state.activations_30d_total} />
        </Grid>

        <Grid item xs={12}>
          <MonthlyLineChart />
        </Grid>
      </Grid>
    </div>
  );
}
